import React, { useMemo, useReducer, useRef, useState } from 'react'
import { Plus, Minus, Sigma } from 'lucide-react'
import { cn } from '../../utils/cn'
import { evaluateTable, colLabel } from '../../utils/formula'

/**
 * TableBlock — an embedded spreadsheet.
 *
 * Cells show their computed value; focusing a cell reveals the raw text or
 * formula for editing. Formulas start with "=" and support cell refs (A1),
 * ranges (A1:B5), + - * / ^ %, comparisons, and SUM AVG MIN MAX COUNT ROUND
 * ABS IF. Errors render honestly (#DIV/0!, #REF!, #CYCLE!, #ERROR!) — never a
 * fabricated number.
 */
export default function TableBlock({ block, onChange }) {
  const [, force] = useReducer((x) => x + 1, 0)
  const [focused, setFocused] = useState(null)   // { r, c } | null
  const cellRefs = useRef(new Map())             // "r,c" → input el

  const rows = block.table.rows
  const nRows = rows.length
  const nCols = Math.max(1, ...rows.map((r) => r.length))

  const { display } = useMemo(
    () => evaluateTable(rows),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(rows)]
  )

  const mutate = (fn) => { fn(); onChange(); force() }

  const setCell = (r, c, v) => mutate(() => {
    while (rows[r].length <= c) rows[r].push('')
    rows[r][c] = v
  })

  const addRow = () => mutate(() => rows.push(new Array(nCols).fill('')))
  const delRow = () => { if (nRows > 1) mutate(() => rows.pop()) }
  const addCol = () => { if (nCols < 26) mutate(() => rows.forEach((r) => r.push(''))) }
  const delCol = () => { if (nCols > 1) mutate(() => rows.forEach((r) => r.splice(nCols - 1))) }

  const focusCell = (r, c) => {
    const el = cellRefs.current.get(`${r},${c}`)
    if (el) { el.focus(); el.select?.() }
  }

  const onCellKeyDown = (e, r, c) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (r === nRows - 1) addRow()
      requestAnimationFrame(() => focusCell(r + 1, c))
    } else if (e.key === 'Tab') {
      e.preventDefault()
      if (e.shiftKey) {
        if (c > 0) focusCell(r, c - 1)
        else if (r > 0) focusCell(r - 1, nCols - 1)
      } else {
        if (c === nCols - 1 && nCols < 26) addCol()
        requestAnimationFrame(() => focusCell(r, c + 1))
      }
    } else if (e.key === 'ArrowDown' && r < nRows - 1) {
      e.preventDefault(); focusCell(r + 1, c)
    } else if (e.key === 'ArrowUp' && r > 0) {
      e.preventDefault(); focusCell(r - 1, c)
    } else if (e.key === 'Escape') {
      e.currentTarget.blur()
    }
  }

  return (
    <div className="group/table my-1">
      <div className="overflow-x-auto rounded-xl border border-ink-150 bg-white">
        <table className="border-collapse w-max min-w-full">
          <thead>
            <tr>
              <th className="w-8 bg-ink-50/80 border-b border-r border-ink-100" />
              {Array.from({ length: nCols }, (_, c) => (
                <th
                  key={c}
                  className="bg-ink-50/80 border-b border-r border-ink-100 px-2 py-1 text-[10px] font-semibold text-ink-400 tracking-wider min-w-[96px] text-center select-none"
                >
                  {colLabel(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={r}>
                <td className="bg-ink-50/80 border-b border-r border-ink-100 px-1.5 text-[10px] text-ink-400 text-center tabular-nums select-none">
                  {r + 1}
                </td>
                {Array.from({ length: nCols }, (_, c) => {
                  const raw = row[c] ?? ''
                  const shown = display[r]?.[c] ?? ''
                  const isFocused = focused?.r === r && focused?.c === c
                  const isError = shown.startsWith('#')
                  const isNumber = !isFocused && shown !== '' && !isError && !Number.isNaN(Number(shown.replace(/,/g, '')))
                  return (
                    <td key={c} className="border-b border-r border-ink-100 p-0">
                      <input
                        ref={(el) => { if (el) cellRefs.current.set(`${r},${c}`, el); else cellRefs.current.delete(`${r},${c}`) }}
                        value={isFocused ? raw : shown}
                        onChange={(e) => setCell(r, c, e.target.value)}
                        onFocus={() => setFocused({ r, c })}
                        onBlur={() => setFocused(null)}
                        onKeyDown={(e) => onCellKeyDown(e, r, c)}
                        spellCheck={false}
                        className={cn(
                          'w-full min-w-[96px] bg-transparent px-2 py-1.5 text-sm outline-none border-0',
                          'focus:bg-accent-50/50 focus:ring-2 focus:ring-inset focus:ring-accent-300',
                          isNumber ? 'text-right tabular-nums text-ink-800' : 'text-ink-700',
                          isError && 'text-loss-600 font-medium',
                          !isFocused && raw.startsWith('=') && !isError && 'text-accent-700',
                        )}
                      />
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Toolbar — appears on hover/focus */}
      <div className="flex items-center gap-1 mt-1.5 opacity-0 group-hover/table:opacity-100 focus-within:opacity-100 transition-opacity duration-200">
        <button onClick={addRow} className="ws-table-btn" title="Add row"><Plus className="w-3 h-3" /> Row</button>
        <button onClick={delRow} disabled={nRows <= 1} className="ws-table-btn" title="Remove last row"><Minus className="w-3 h-3" /> Row</button>
        <span className="w-px h-3.5 bg-ink-150 mx-0.5" />
        <button onClick={addCol} disabled={nCols >= 26} className="ws-table-btn" title="Add column"><Plus className="w-3 h-3" /> Col</button>
        <button onClick={delCol} disabled={nCols <= 1} className="ws-table-btn" title="Remove last column"><Minus className="w-3 h-3" /> Col</button>
        <span className="inline-flex items-center gap-1 ml-2 text-[10px] text-ink-400 select-none">
          <Sigma className="w-3 h-3" />
          = starts a formula · SUM(A1:B2) · AVG · MIN · MAX · COUNT · ROUND · IF
        </span>
      </div>
    </div>
  )
}
